<template>
  <s-modal
    v-model="showModalHandler"
    cancel-confirm-message-code="MC006"
    :title="$t('reports.variables-and-reference')"
    :ok-title="$t('general.conclude')"
    :ok-disabled="variableIsEmpty || (referenceIsEmpty && userReferenceHandler && hasReferences)"
    :is-loading="isLoading"
    :confirm-type="confirmationTypes.YES_NO"
    @cancel="closeModal"
    @ok="addAdjustVariables"
    :spaced="true"
  >
    <b-card class="variables-and-reference">
      <b-container fluid>
        <b-row>
          <label class="variables-and-reference__input-group-title">
            {{ $t('reports.enter-oscillography-variables')}}
          </label>
        </b-row>
        <b-row align-h="center" class="variables-and-reference__grid mt-3">
          <s-loader v-if="loading" size="lg" />
          <grid
            v-else
            :backup="variablesHandler"
            :items="variables"
            :tableHeader="$t('entities.variables')"
            :tableLabel="$t('reports.step-variables.first-table')"
            @input="handleVariablesSelection"
          />
        </b-row>
        <div v-if="hasReferences">
          <b-row class="mt-5">
            <label class="variables-and-reference__input-group-title">
              {{ $t('reports.enter-oscillography-references') }}
            </label>
          </b-row>
          <b-row align-v="end">
            <b-col cols="auto">
              <s-switch v-model="userReferenceHandler" />
            </b-col>
            <b-col
              cols="auto"
              class="mt-1"
            > {{ $t('reports.step-variables.tooltip-label') }}.
              <i
                aria-hidden="true"
                class="fas fa-info-circle variables-and-reference__tooltip-icon--blue"
                id="variables-and-reference__tooltip"
              />
                <b-tooltip
                  placement="right"
                  boundary="viewport"
                  target="variables-and-reference__tooltip"
                >
                  <span>
                    {{ $t('reports.step-variables.tooltip-hint') }}.
                  </span>
                </b-tooltip>
            </b-col>
          </b-row>
            <b-row
              class="mt-3"
              v-if="userReferenceHandler"
            >
              <grid
                :items="references"
                :backup="referencesHandler"
                :tableHeader="$t('general.references')"
                :tableLabel="$t('reports.step-variables.second-table')"
                @input="handleReferencesSelection"
              />
            </b-row>
        </div>
      </b-container>
    </b-card>
  </s-modal>
</template>

<script>
import OscillographyService from '@/domain/services/generalPanel/reports/oscillography';
import confirmationTypes from '@/constants/confirmationTypes';
import Grid from '@/domain/components/generalPanel/reports/oscillography/newReport/variablesAndReference/Grid';

export default {
  name: 'VariablesAndReference',
  components: {
    Grid,
  },
  props: {
    assetTypeId: {
      required: true,
      type: String,
    },
    maneuverType: {
      required: true,
      type: Array,
      default: () => [],
    },
    moduloId: {
      required: true,
      type: String,
    },
    showModal: {
      type: Boolean,
    },
    isLoading: {
      type: Boolean,
    },
    backup: {
      type: Object,
      default: () => ({
        variables: [],
        referenceVariables: [],
      }),
    },
  },
  data: () => ({
    confirmationTypes,
    loading: false,
    userReference: true,
    variables: [],
    references: [],
    variableIsEmpty: true,
    referenceIsEmpty: true,
    variableChosed: [],
    referenceChosed: [],
    initializedReferences: false,
  }),
  watch: {
    assetTypeId() {
      this.initializedReferences = false;
    },
    moduloId() {
      this.initializedReferences = false;
    },
  },
  computed: {
    userReferenceHandler: {
      get() {
        const fromBackup = (this.backup && Object.prototype.hasOwnProperty.call(this.backup, 'useOscillographyReference'))
          ? this.backup.useOscillographyReference
          : this.userReference;
        return fromBackup && !this.loading && this.hasReferences;
      },
      set(value) {
        if (!this.hasReferences) {
          this.userReference = false;
          return;
        }

        this.userReference = value;
        if (!value) {
          this.clearReferences();
          this.initializedReferences = false;
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!this.initializedReferences && this.references?.length) {
            this.referenceChosed = this.references.map(r => r.id ?? r);
            this.$emit('references-changed', this.referenceChosed);
            this.initializedReferences = true;
          }
        }
      },
    },

    variablesHandler: {
      get() {
        this.isVariableEmpty();
        return this.backup.variables;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    referencesHandler: {
      get() {
        this.isReferenceEmpty();
        return this.backup.referenceVariables.map((x) => x.toUpperCase());
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    showModalHandler: {
      get() {
        this.getVariables();
        return this.showModal;
      },
      set(val) {
        if (!val) {
          this.closeModal();
        }
      },
    },
    backupVariables() {
      return this.backup.variables || [];
    },
    backupReferenceVariables() {
      return this.backup.referenceVariables || [];
    },
    hasReferences() {
      return this.references && this.references.length > 0;
    },
  },
  created() {
    this.getVariables();
  },
  methods: {
    isVariableEmpty() {
      if (this.backup.variables !== '') {
        this.variableIsEmpty = false;
      } else {
        this.variableIsEmpty = true;
      }
    },
    isReferenceEmpty() {
      if (this.backup.referenceVariables !== '') {
        this.referenceIsEmpty = false;
      } else {
        this.referenceIsEmpty = true;
      }
    },
    closeModal() {
      this.$emit('close-modal');
    },
    handleReferencesSelection(newSelection) {
      this.referenceChosed = newSelection;
      this.backup.referenceVariables = newSelection;

      if (newSelection.length > 0) {
        this.referenceIsEmpty = false;
      } else {
        this.referenceIsEmpty = true;
      }
    },
    handleVariablesSelection(newSelection) {
      this.variableChosed = newSelection;
      this.backup.variables = newSelection;

      if (newSelection.length > 0) {
        this.variableIsEmpty = false;
      } else {
        this.variableIsEmpty = true;
      }
    },
    getVariables() {
      if (this.assetTypeId !== '' && this.maneuverType !== '') {
        this.loading = true;
        const { assetTypeId, maneuverType, moduloId } = this;
        OscillographyService
          .getVariables({ assetTypeId, maneuverType, moduloId })
          .then(({ variables, referenceVariables }) => {
            this.variables = variables;
            if (this.backup.variables?.length === 0) {
              this.backup.variables = variables.map((x) => x.id);
              this.$emit('variables-changed', this.backup.variables);
            }
            this.references = referenceVariables;

            const useRef = (this.backup && Object.prototype.hasOwnProperty.call(this.backup, 'useOscillographyReference'))
              ? this.backup.useOscillographyReference
              : this.userReference;
            if (useRef
              && referenceVariables?.length
              && !this.initializedReferences
              && this.backup.referenceVariables?.length === 0) {
              this.referenceChosed = referenceVariables.map(r => r.id ?? r);
              this.variableChosed = variables.map(v => v.id ?? v);
              this.$emit('references-changed', this.referenceChosed);
              this.$emit('variables-changed', this.variableChosed);
              this.initializedReferences = true;
            }
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    clearReferences() {
      this.$emit('references-changed', []);
      this.referenceChosed = [];
    },
    addAdjustVariables() {
      const variablesToEmit = this.variableChosed.length > 0
        ? this.variableChosed
        : this.backup.variables;

      let referencesToEmit = [];
      if (this.hasReferences) {
        referencesToEmit = this.referenceChosed.length > 0
          ? this.referenceChosed
          : this.backup.referenceVariables;
      }

      this.$emit('variables-changed', variablesToEmit);
      this.$emit('references-changed', referencesToEmit);
      this.$emit('save');
      this.closeModal();
    },
  },
};
</script>


<style lang="sass" scoped>
.variables-and-reference
  background-color: var(--color-background-regular)
  &__tooltip-icon--blue
    margin-left: 5px
    color: var(--color-info)
  &__asset-name
    color: var(--color-gray-medium-dark)
  &__grid-list
    overflow-y: auto
    max-height: 45vh
  &__input-group-title
    color: var(--color-secondary)
  &__asset-item
    height: 35px
    display: flex
    align-items: center
    padding-left: 10px
    &-pair
      background-color: var(--color-table-header-bg)
    &-odd
      background-color: var(--color-background-light)
  &__asset-header
    font-weight: bold
    height: 45px
    background-color: var(--color-gray-medium-light)
    display: flex
    align-items: center
    padding-left: 10px
</style>
